import React, { forwardRef, useState, useEffect, useRef, JSX } from 'react';
import { Box, Button, Tooltip, SxProps, Typography } from '@mui/material';
import { Send as SendIcon, Person as PersonIcon } from '@mui/icons-material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { useAuth } from 'hooks/AuthContext';
import {
  ChatMessage,
  ChatSession,
  ChatMessageUser,
  ChatMessageError,
  ChatMessageStreaming,
  ChatMessageStatus,
  ChatMessageMetaData,
} from 'types/types';
import { ConversationHandle } from 'components/Conversation';
import { Message } from 'components/Message';
import { DeleteConfirmation } from 'components/DeleteConfirmation';
import { useAppState, useSelectedCandidate } from 'hooks/GlobalContext';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { BackstoryTextField, BackstoryTextFieldRef } from 'components/BackstoryTextField';
import { Scrollable } from 'components/Scrollable';
import { StyledMarkdown } from '../StyledMarkdown';

const emptyMetadata: ChatMessageMetaData = {
  model: 'qwen2.5',
  temperature: 0,
  maxTokens: 0,
  topP: 0,
  frequencyPenalty: 0,
  presencePenalty: 0,
  stopSequences: [],
  usage: {
    evalCount: 0,
    evalDuration: 0,
    promptEvalCount: 0,
    promptEvalDuration: 0,
  },
};

const defaultMessage: ChatMessage = {
  status: 'done',
  type: 'text',
  sessionId: '',
  timestamp: new Date(),
  content: '',
  role: 'user',
  metadata: emptyMetadata,
};

interface ResumeChatProps {
  onResumeChange: (prompt: string, resume: string) => void; // Callback when the resume changes
  resume: string;
  session: string; // Session identifier for the chat
  sx?: SxProps; // Optional styles for the component
}

const ResumeChat = forwardRef<ConversationHandle, ResumeChatProps>(
  (props: ResumeChatProps, ref): JSX.Element => {
    const {
      session,
      onResumeChange,
      resume,
      sx,
      // tunables = {
      //   enableRAG: true,
      //   enableTools: true,
      //   enableContext: true,
      //   topK: 50,
      //   temperature: 0.7,
      //   threshold: 0.75,
      // },
    } = props;
    const { apiClient } = useAuth();
    const { selectedCandidate } = useSelectedCandidate();
    const [processingMessage, setProcessingMessage] = useState<
      ChatMessageStatus | ChatMessageError | null
    >(null);
    const [streamingMessage, setStreamingMessage] = useState<ChatMessage | null>(null);
    const backstoryTextRef = useRef<BackstoryTextFieldRef>(null);

    const { setSnack } = useAppState();

    const [chatSession, setChatSession] = useState<ChatSession | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [streaming, setStreaming] = useState<boolean>(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const onDelete = async (session: ChatSession): Promise<void> => {
      if (!session.id) {
        return;
      }
      try {
        await apiClient.resetChatSession(session.id);
        // If we're deleting the currently selected session, clear it
        setMessages([]);
        setSnack('Session reset succeeded', 'success');
      } catch (error) {
        console.error('Failed to delete session:', error);
        setSnack('Failed to delete session', 'error');
      }
    };

    // Send message
    const sendMessage = async (message: string): Promise<void> => {
      if (!message.trim() || !chatSession?.id || streaming || !selectedCandidate) return;

      const messageContent = message;
      setStreaming(true);

      const chatMessage: ChatMessageUser = {
        sessionId: chatSession.id,
        role: 'user',
        content: messageContent,
        status: 'done',
        type: 'text',
        extraContext: { resume: resume },
        timestamp: new Date(),
      };

      setProcessingMessage({
        ...defaultMessage,
        status: 'status',
        activity: 'info',
        content: `Establishing connection with ${selectedCandidate.firstName}'s chat session.`,
      });

      setMessages(prev => {
        return [...prev, chatMessage] as ChatMessage[];
      });

      try {
        apiClient.sendMessageStream(chatMessage, {
          onMessage: (msg: ChatMessage): void => {
            if (msg.content.match(/^ANSWER:\s*/)) {
              msg.content = msg.content.replace(/^ANSWER:\s*/, '');
              msg.extraContext = {
                isAnswer: true,
              };
            } else {
              onResumeChange(message, msg.content);
            }

            setMessages(prev => {
              const filtered = prev.filter(m => m.id !== msg.id);
              return [...filtered, msg] as ChatMessage[];
            });
            setStreamingMessage(null);
            setProcessingMessage(null);
          },
          onError: (error: string | ChatMessageError): void => {
            console.log('onError:', error);
            // Type-guard to determine if this is a ChatMessageBase or a string
            if (typeof error === 'object' && error !== null && 'content' in error) {
              setProcessingMessage(error);
            } else {
              setProcessingMessage({
                ...defaultMessage,
                status: 'error',
                content: error,
              });
            }
            setStreaming(false);
          },
          onStreaming: (chunk: ChatMessageStreaming): void => {
            // console.log("onStreaming:", chunk);
            // Close out any in-progress "thinking" bubble now that the
            // model has moved on to producing its actual response.
            setProcessingMessage(null);
            const prefix = 'ANSWER:';
            let content = chunk.content || '';
            for (let i = prefix.length; i >= 1; i--) {
              if (chunk.content.startsWith(prefix.slice(0, i))) {
                content = chunk.content.slice(i);
                break;
              }
            }
            content = content.trim();
            if (streamingMessage?.content === content) {
              return; // Avoid duplicate updates
            }
            setStreamingMessage({
              ...chunk,
              content,
              role: 'assistant',
              metadata: emptyMetadata,
            });
          },
          onStatus: (status: ChatMessageStatus): void => {
            setProcessingMessage(status);
          },
          onComplete: (): void => {
            console.log('onComplete');
            setStreamingMessage(null);
            setProcessingMessage(null);
            setStreaming(false);
          },
        });
      } catch (error) {
        console.error('Failed to send message:', error);
        setStreaming(false);
      }
    };

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Load sessions when username changes
    useEffect(() => {
      if (!selectedCandidate) {
        setSnack('Candidate not selected', 'error');
        return;
      }
      try {
        setLoading(true);
        apiClient
          .getOrCreateChatSession(selectedCandidate, session, 'edit_resume')
          .then(session => {
            setChatSession(session);
            setLoading(false);
          });
      } catch (error) {
        setSnack('Unable to load chat session', 'error');
      } finally {
        setLoading(false);
      }
    }, [selectedCandidate, apiClient, setSnack]);

    // Load messages when session changes
    useEffect(() => {
      const loadMessages = async (): Promise<void> => {
        if (!chatSession?.id) return;

        try {
          const result = await apiClient.getChatMessages(chatSession.id);
          const chatMessages: ChatMessage[] = result.data;
          console.log(`getChatMessages returned ${chatMessages.length} messages.`, chatMessages);
          chatMessages.forEach(msg => {
            if (msg.role === 'assistant' && msg.content.match(/^ANSWER:\s*/)) {
              msg.content = msg.content.replace(/^ANSWER: */, '');
              msg.extraContext = {
                isAnswer: true,
              };
            }
          });

          setMessages(chatMessages);
          setProcessingMessage(null);
          setStreamingMessage(null);
        } catch (error) {
          console.error('Failed to load messages:', error);
        }
      };

      if (chatSession?.id) {
        loadMessages();
      }
    }, [chatSession, apiClient]);

    return (
      <Box
        className="ResumeChat"
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%' /* Restrict to main-container's height */,
          width: '100%',
          maxWidth: '100%',
          minHeight: 0 /* Prevent flex overflow */,
          maxHeight: 'min-content',
          '& > *:not(.Scrollable)': {
            flexShrink: 0 /* Prevent shrinking */,
          },
          position: 'relative',
          ...sx,
        }}
      >
        {/* Chat Interface */}
        {/* Scrollable Messages Area */}
        {chatSession && (
          <Scrollable
            sx={{
              position: 'relative',
              boxSizing: 'border-box',
              maxHeight: '100%',
              maxWidth: '100%',
              width: '100%',
              display: 'flex',
              flexGrow: 1,
              flex: 1 /* Take remaining space in some-container */,
              overflowY: 'auto' /* Scroll if content overflows */,
              p: 0,
              gap: 1,
            }}
          >
            {messages.map((message: ChatMessage) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  maxWidth: '100%',
                  position: 'relative',
                  '& pre, & > p': { border: 0, mt: '0.25rem', mb: 0 },
                }}
              >
                {message.role === 'user' && (
                  <>
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography>{message.content}</Typography>
                  </>
                )}
                {message.role === 'assistant' && (
                  <>
                    {message.extraContext?.isAnswer ? (
                      <>
                        <QuestionAnswerIcon sx={{ mr: 1 }} />
                        <StyledMarkdown content={message.content} />
                      </>
                    ) : (
                      <>
                        <PrecisionManufacturingIcon sx={{ mr: 1 }} />
                        <StyledMarkdown content="Resume updated." />
                      </>
                    )}
                  </>
                )}
              </Box>
            ))}
            {processingMessage !== null && (
              <Message {...{ chatSession, message: processingMessage }} />
            )}
            {streamingMessage !== null && (
              <Box sx={{ '& pre': { border: 0 } }}>
                <StyledMarkdown content={streamingMessage.content} streaming={true} />
              </Box>
            )}
            {streaming && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  m: 1,
                }}
              >
                <PropagateLoader
                  size="10px"
                  loading={streaming}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Scrollable>
        )}
        {/* Fixed Message Input */}
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            gap: 1,
            position: 'relative',
            width: '100%',
            minWidth: '100%',
            flexDirection: 'row',
            overflow: 'hidden',
          }}
        >
          <DeleteConfirmation
            onDelete={(): void => {
              chatSession && onDelete(chatSession);
            }}
            disabled={!chatSession}
            sx={{ minWidth: 'auto', px: 2, maxHeight: 'min-content' }}
            action="reset"
            label="chat session"
            title="Reset Chat Session"
            message={`Are you sure you want to reset the session? This action cannot be undone.`}
          />
          <BackstoryTextField
            placeholder="What would you like to change about this resume?"
            ref={backstoryTextRef}
            onEnter={sendMessage}
            disabled={streaming || loading}
          />
          <Tooltip title="Send">
            <span
              style={{
                minWidth: 'auto',
                maxHeight: 'min-content',
                alignSelf: 'center',
              }}
            >
              <Button
                variant="contained"
                onClick={(): void => {
                  sendMessage(
                    (backstoryTextRef.current && backstoryTextRef.current.getAndResetValue()) || ''
                  );
                }}
                disabled={streaming || loading}
              >
                <SendIcon />
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  }
);
ResumeChat.displayName = 'ResumeChat';
export { ResumeChat };
